import SimplePeer, { SignalData } from 'simple-peer'

import { isUsingRelay } from '../utils-webrtc'

interface WebRtcPeerOptions<TData> extends SimplePeer.Options {
  onSignal?(data: SignalData): void
  onTrack?(track: MediaStreamTrack, stream: MediaStream): void
  onData?(data: TData): void
  onConnect?(): void
  onClose?(): void
  onError?(error: Error, peer: WebRtcPeer<TData>): void
  onIceStateChange?(state: RTCIceConnectionState): void
}

export interface WebRtcPeer<TData> extends SimplePeer.Instance {
  sendData(data: TData): void
  isUsingRelay(): Promise<boolean>
}

export function createWebRtcPeer<TData = any>(options: WebRtcPeerOptions<TData>): WebRtcPeer<TData> {
  const { onSignal, onTrack, onData, onConnect, onClose, onError, onIceStateChange, ...rest } = options

  const simplePeer = new SimplePeer(rest)
  const peer = (simplePeer as unknown) as WebRtcPeer<TData>

  peer.sendData = (data: TData) => {
    if (simplePeer.connected) simplePeer.send(JSON.stringify(data))
  }
  peer.isUsingRelay = async () => await isUsingRelay(peer)

  const onDataEnconded = (encodedData: string) => {
    onData?.(JSON.parse(encodedData) as TData)
  }

  onSignal && peer.on('signal', onSignal)
  onTrack && peer.on('track', onTrack)
  onData && peer.on('data', onDataEnconded)
  onConnect && peer.on('connect', onConnect)
  onClose && peer.on('close', onClose)
  onError && peer.on('error', (error) => onError(error, peer))
  onIceStateChange && peer.on('iceStateChange', (state: RTCIceConnectionState) => onIceStateChange(state))

  return peer
}
